import { useState } from "react";
import { AlertTriangle, Building, Plus } from "lucide-react";
import {
    Button,
    Container,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogForm,
    Field,
    FieldError,
    FieldGroup,
    FieldLabel,
    FieldSet,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    TableSkeleton,
    Text,
    Textarea,
    Tabs,
    Heading,
    PageHeader,
    PageHeaderIdentity,
    PageHeaderInfo,
    PageHeaderMeta,
    PageHeaderMetaDot,
    PageHeaderMetaItem,
    PageHeaderRow,
    PageHeaderTitleRow,
    TabsList,
    TabsTrigger,
    TabsContent,
    PageHeaderActions,
} from "@athena/ui";
import { useQuery, useMutation } from "@apollo/client/react";
import { toast } from "sonner";
import {
    CrisisResourcesDocument,
    CreateCrisisKeywordDocument,
    UpdateCrisisKeywordDocument,
    DeleteCrisisKeywordDocument,
    CreateCrisisResourceDocument,
    UpdateCrisisResourceDocument,
    DeleteCrisisResourceDocument,
    type CrisisResourcesQuery,
} from "../graphql/crisis.generated";
import type { CrisisSeverity } from "@athena/common/graphql/generated/schema";
import { PAGE_SIZE } from "../../../global/constants";
import { useListView } from "@/hooks/useListView";
import { useSearchParams } from "react-router-dom";
import { CrisisWords } from "./CrisisManagement/CrisisWords";
import { CrisisResources } from "./CrisisManagement/CrisisResources";
import { DeleteDialog } from "@/components/DeleteDialog";
import { useTranslation } from "react-i18next";

type CrisisResource = CrisisResourcesQuery["crisisResources"][number];
type CrisisKeyword = CrisisResource["keywords"][number];

type CrisisEntry = { keyword: CrisisKeyword; resource: CrisisResource };

export function CrisisManagementPage() {
    const { t } = useTranslation();
    const [
        searchParams,
        setSearchParams,
    ] = useSearchParams();
    const activeTab = searchParams.get("tab") ?? "words";
    const [
        search,
        setSearch,
    ] = useState<string>("");
    const [
        severityFilter,
        setSeverityFilter,
    ] = useState<"all" | CrisisSeverity>("all");
    const [
        selected,
        setSelected,
    ] = useState<Set<string>>(new Set());
    const [
        newOpen,
        setNewOpen,
    ] = useState<boolean>(false);
    const [
        newSeverity,
        setNewSeverity,
    ] = useState<CrisisSeverity>("MEDIUM");
    const [
        newResourceId,
        setNewResourceId,
    ] = useState<string>("");
    const [
        editKeyword,
        setEditKeyword,
    ] = useState<CrisisKeyword | null>(null);
    const [
        editValue,
        setEditValue,
    ] = useState<string>("");
    const [
        editSeverity,
        setEditSeverity,
    ] = useState<CrisisSeverity>("MEDIUM");
    const [
        editResourceId,
        setEditResourceId,
    ] = useState<string>("");
    const [
        editError,
        setEditError,
    ] = useState<string | null>(null);
    const [
        deleteKeywordTarget,
        setDeleteKeywordTarget,
    ] = useState<CrisisKeyword | null>(null);
    const [
        resourceSearch,
        setResourceSearch,
    ] = useState<string>("");
    const [
        selectedResources,
        setSelectedResources,
    ] = useState<Set<string>>(new Set());
    const [
        newResourceOpen,
        setNewResourceOpen,
    ] = useState<boolean>(false);
    const [
        newResourceMessage,
        setNewResourceMessage,
    ] = useState<string>("");
    const [
        newResourceEmail,
        setNewResourceEmail,
    ] = useState<string>("");
    const [
        newResourcePhoneNumber,
        setNewResourcePhoneNumber,
    ] = useState<string>("");
    const [
        newResourceError,
        setNewResourceError,
    ] = useState<string | null>(null);
    const [
        editResource,
        setEditResource,
    ] = useState<CrisisResource | null>(null);
    const [
        editResourceMessage,
        setEditResourceMessage,
    ] = useState<string>("");
    const [
        editResourceEmail,
        setEditResourceEmail,
    ] = useState<string>("");
    const [
        editResourcePhoneNumber,
        setEditResourcePhoneNumber,
    ] = useState<string>("");
    const [
        editResourceError,
        setEditResourceError,
    ] = useState<string | null>(null);
    const [
        deleteResourceTarget,
        setDeleteResourceTarget,
    ] = useState<CrisisResource | null>(null);

    const { data, loading, error } = useQuery(CrisisResourcesDocument);
    const [createKeyword] = useMutation(CreateCrisisKeywordDocument);
    const [
        updateKeyword,
        { loading: updating },
    ] = useMutation(UpdateCrisisKeywordDocument);
    const [
        deleteKeyword,
        { loading: deleting },
    ] = useMutation(DeleteCrisisKeywordDocument);
    const [
        createResource,
        { loading: creatingResource },
    ] = useMutation(CreateCrisisResourceDocument);
    const [
        updateResource,
        { loading: updatingResource },
    ] = useMutation(UpdateCrisisResourceDocument);
    const [
        deleteResource,
        { loading: deletingResource },
    ] = useMutation(DeleteCrisisResourceDocument);

    const resources: CrisisResource[] = data?.crisisResources ?? [];
    const entries: CrisisEntry[] = resources.flatMap((resource) =>
        resource.keywords.map((keyword) => ({ keyword, resource })),
    );

    const filteredWords = entries
        .filter((e) => {
            const matchesSearch =
                e.keyword.value.toLowerCase().includes(search.toLowerCase()) ||
                e.resource.message.toLowerCase().includes(search.toLowerCase());
            const matchesSeverity = severityFilter === "all" || e.keyword.severity === severityFilter;
            return matchesSearch && matchesSeverity;
        })
        .sort((a, b) => a.keyword.value.localeCompare(b.keyword.value, undefined, { sensitivity: "base" }));

    const filteredResources: CrisisResource[] = resources
        .filter((r) => {
            const query = resourceSearch.toLowerCase();
            return r.message.toLowerCase().includes(query) || (r.email ?? "").toLowerCase().includes(query);
        })
        .sort((a, b) => a.message.localeCompare(b.message, undefined, { sensitivity: "base" }));

    /*
     * Scroll or pages, the reader's choice, portal-wide — see `useListView`. Both tabs had their
     * own page state and their own Previous/Next over lists already held whole; this is the same
     * slice with one control, and above the guards below because it calls hooks.
     */
    const wordsView = useListView(filteredWords, { pageSize: PAGE_SIZE });
    const resourcesView = useListView(filteredResources, { pageSize: PAGE_SIZE });

    // Keyed on data, not `loading`: Apollo sets loading on every refetch, so gating
    // the whole page on it replaces live content — and unmounts any child holding
    // state or an in-flight request — every time something reloads.
    if (loading && !data) return <TableSkeleton />;
    if (error) {
        return (
            <Container className="p-6">
                <div className="flex flex-col items-center justify-center gap-3 py-32">
                    <AlertTriangle />
                    <Text weight="medium">{t("errors.failedToLoad", { variable: "crisis" })}</Text>
                    <Text muted className="text-sm">
                        {t("errors.wentWrong")}
                    </Text>
                </div>
            </Container>
        );
    }

    const openEdit = (keyword: CrisisKeyword) => {
        setEditKeyword(keyword);
        setEditValue(keyword.value);
        setEditSeverity(keyword.severity);
        setEditResourceId(keyword.crisisResourceId);
        setEditError(null);
    };

    const handleUpdate = async () => {
        if (!editKeyword || !editValue.trim()) return;
        setEditError(null);
        try {
            await updateKeyword({
                variables: {
                    id: editKeyword.id,
                    input: { value: editValue.trim(), severity: editSeverity, crisisResourceId: editResourceId },
                },
                optimisticResponse: {
                    updateCrisisKeyword: {
                        __typename: "CrisisKeyword",
                        id: editKeyword.id,
                        value: editValue.trim(),
                        severity: editSeverity,
                        crisisResourceId: editResourceId,
                    },
                },
            });
            setEditKeyword(null);
        } catch (error) {
            setEditError(error instanceof Error ? error.message : "Failed to save. Please try again.");
        }
    };

    const handleDelete = async () => {
        if (!deleteKeywordTarget) return;
        await deleteKeyword({
            variables: { id: deleteKeywordTarget.id },
            update(cache) {
                cache.evict({ id: cache.identify({ __typename: "CrisisKeyword", id: deleteKeywordTarget.id }) });
                cache.gc();
            },
        });
        setSelected((prev) => {
            const next = new Set(prev);
            next.delete(deleteKeywordTarget.id);
            return next;
        });
        setDeleteKeywordTarget(null);
    };

    const handleCreate = async ({ name }: { name: string; title: string; bio: string }) => {
        const resourceId = newResourceId || resources[0]?.id;
        if (!resourceId) return;
        try {
            await createKeyword({
                variables: { input: { value: name.trim(), severity: newSeverity, crisisResourceId: resourceId } },
                refetchQueries: [CrisisResourcesDocument],
            });
        } catch (error) {
            toast.error(error instanceof Error ? error.message : "Failed to add keyword. Please try again.");
            throw error;
        }
    };

    const defaultResourceId = resources[0]?.id ?? "";

    const openEditResource = (resource: CrisisResource) => {
        setEditResource(resource);
        setEditResourceMessage(resource.message);
        setEditResourceEmail(resource.email ?? "");
        setEditResourcePhoneNumber(resource.phoneNumber ?? "");
        setEditResourceError(null);
    };

    const handleCreateResource = async () => {
        if (!newResourceMessage.trim()) return;
        setNewResourceError(null);
        try {
            await createResource({
                variables: {
                    input: {
                        message: newResourceMessage.trim(),
                        email: newResourceEmail.trim() || undefined,
                        phoneNumber: newResourcePhoneNumber.trim() || undefined,
                    },
                },
                refetchQueries: [CrisisResourcesDocument],
            });
            setNewResourceOpen(false);
            setNewResourceMessage("");
            setNewResourceEmail("");
            setNewResourcePhoneNumber("");
        } catch (error) {
            setNewResourceError(error instanceof Error ? error.message : "Failed to add resource. Please try again.");
        }
    };

    const handleUpdateResource = async () => {
        if (!editResource || !editResourceMessage.trim()) return;
        setEditResourceError(null);
        try {
            await updateResource({
                variables: {
                    id: editResource.id,
                    input: {
                        message: editResourceMessage.trim(),
                        email: editResourceEmail.trim() || null,
                        phoneNumber: editResourcePhoneNumber.trim() || null,
                    },
                },
                optimisticResponse: {
                    updateCrisisResource: {
                        __typename: "CrisisResource",
                        id: editResource.id,
                        message: editResourceMessage.trim(),
                        email: editResourceEmail.trim() || null,
                        phoneNumber: editResourcePhoneNumber.trim() || null,
                    },
                },
            });
            setEditResource(null);
        } catch (error) {
            setEditResourceError(error instanceof Error ? error.message : "Failed to save. Please try again.");
        }
    };

    const handleDeleteResource = async () => {
        if (!deleteResourceTarget) return;
        await deleteResource({
            variables: { id: deleteResourceTarget.id },
            update(cache) {
                cache.evict({ id: cache.identify({ __typename: "CrisisResource", id: deleteResourceTarget.id }) });
                cache.gc();
            },
        });
        setSelectedResources((prev) => {
            const next = new Set(prev);
            next.delete(deleteResourceTarget.id);
            return next;
        });
        setDeleteResourceTarget(null);
    };

    return (
        <Tabs value={activeTab} onValueChange={(tab) => setSearchParams({ tab }, { replace: true })}>
            <PageHeader className="pb-0">
                <PageHeaderRow>
                    <PageHeaderIdentity>
                        <PageHeaderInfo>
                            <PageHeaderTitleRow>
                                <Heading level={3}>{t("admin.crisis.crisisManagement")}</Heading>
                            </PageHeaderTitleRow>
                            <PageHeaderMeta>
                                <PageHeaderMetaItem>{entries.length} keywords</PageHeaderMetaItem>
                                <PageHeaderMetaDot />
                                <PageHeaderMetaItem>{resources.length} crisis resources</PageHeaderMetaItem>
                                <PageHeaderMetaDot />
                            </PageHeaderMeta>
                        </PageHeaderInfo>
                    </PageHeaderIdentity>
                    <PageHeaderActions>
                        {activeTab === "resources" ? (
                            <Button
                                size="sm"
                                className="flex items-center gap-1.5"
                                onClick={() => setNewResourceOpen(true)}
                            >
                                <Plus className="h-3.5 w-3.5" />
                                {t("admin.crisis.newResource")}
                            </Button>
                        ) : (
                            <Button size="sm" className="flex items-center gap-1.5" onClick={() => setNewOpen(true)}>
                                <Plus className="h-3.5 w-3.5" />
                                {t("admin.crisis.newKeyword")}
                            </Button>
                        )}
                    </PageHeaderActions>
                </PageHeaderRow>
                <PageHeaderRow>
                    <TabsList variant="line" className="bg-card">
                        <TabsTrigger variant="line" value="words" className="data-[state=active]:border-secondary">
                            <Building className="text-muted-foreground mr-2 h-4 w-4" />
                            {t("admin.crisis.words")}
                        </TabsTrigger>
                        <TabsTrigger variant="line" value="resources" className="data-[state=active]:border-secondary">
                            <Building className="text-muted-foreground mr-2 h-4 w-4" />
                            {t("admin.crisis.crisisResources")}
                        </TabsTrigger>
                    </TabsList>
                </PageHeaderRow>
            </PageHeader>
            <Container maxWidth="full" className="p-6">
                <TabsContent value="words">
                    <CrisisWords
                        search={search}
                        onSearchChange={setSearch}
                        severityFilter={severityFilter}
                        onSeverityFilterChange={setSeverityFilter}
                        view={wordsView}
                        resultsLabel={
                            filteredWords.length === 0
                                ? "0 results"
                                : `${wordsView.from}–${wordsView.to} of ${filteredWords.length}`
                        }
                        selected={selected}
                        onEdit={openEdit}
                        onDelete={setDeleteKeywordTarget}
                    />
                    {/* New keyword dialog */}
                    <DialogForm
                        open={newOpen}
                        onOpenChange={(open) => {
                            setNewOpen(open);
                            if (!open) {
                                setNewSeverity("MEDIUM");
                                setNewResourceId("");
                            }
                        }}
                        title={t("admin.crisis.newKeyword")}
                        description={t("admin.crisis.keywordDesc")}
                        showAvatar={false}
                        showTitleRole={false}
                        showBio={false}
                        labelName={t("admin.crisis.keyword")}
                        labelNamePlaceholder="e.g. hopeless"
                        labelCancel="Cancel"
                        labelSubmit={t("admin.crisis.addKeyword")}
                        onSubmit={handleCreate}
                    >
                        <div className="px-1">
                            <FieldSet>
                                <FieldGroup>
                                    <Field>
                                        <FieldLabel htmlFor="new-kw-severity">
                                            {t("admin.crisis.sev.severity")}
                                        </FieldLabel>
                                        <Select
                                            value={newSeverity}
                                            onValueChange={(v) => setNewSeverity(v as CrisisSeverity)}
                                        >
                                            <SelectTrigger id="new-kw-severity" className="w-full">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="HIGH">{t("admin.crisis.sev.high")}</SelectItem>
                                                <SelectItem value="MEDIUM">{t("admin.crisis.sev.med")}</SelectItem>
                                                <SelectItem value="LOW">{t("admin.crisis.sev.low")}</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </Field>
                                    <Field>
                                        <FieldLabel htmlFor="new-kw-resource">
                                            {t("admin.crisis.linkedResource")}
                                        </FieldLabel>
                                        <Select
                                            value={newResourceId || defaultResourceId}
                                            onValueChange={setNewResourceId}
                                        >
                                            <SelectTrigger id="new-kw-resource" className="w-full">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {resources.map((r) => (
                                                    <SelectItem key={r.id} value={r.id}>
                                                        {r.phoneNumber ? `${r.phoneNumber} · ` : ""}
                                                        {r.email ?? "No contact info"}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </Field>
                                </FieldGroup>
                            </FieldSet>
                        </div>
                    </DialogForm>

                    {/* Edit keyword dialog */}
                    <Dialog
                        open={!!editKeyword}
                        onOpenChange={(open) => {
                            if (!open) setEditKeyword(null);
                        }}
                    >
                        <DialogContent className="max-w-lg">
                            <DialogHeader>
                                <DialogTitle>{t("admin.crisis.editKeyword")}</DialogTitle>
                                <DialogDescription>{t("admin.crisis.editKeywordDesc")}</DialogDescription>
                            </DialogHeader>
                            <FieldSet>
                                <FieldGroup>
                                    <Field data-invalid={!!editError}>
                                        <FieldLabel htmlFor="edit-kw-value" required>
                                            {t("admin.crisis.keyword")}
                                        </FieldLabel>
                                        <Input
                                            id="edit-kw-value"
                                            value={editValue}
                                            onChange={(e) => {
                                                setEditValue(e.target.value);
                                                setEditError(null);
                                            }}
                                            aria-invalid={!!editError}
                                            autoFocus
                                            type="text"
                                        />
                                        <FieldError errors={editError ? [{ message: editError }] : undefined} />
                                    </Field>
                                    <Field>
                                        {t("admin.crisis.sev.severity")}
                                        <Select
                                            value={editSeverity}
                                            onValueChange={(v) => setEditSeverity(v as CrisisSeverity)}
                                        >
                                            <SelectTrigger id="edit-kw-severity" className="w-full">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="HIGH">{t("admin.crisis.sev.high")}</SelectItem>
                                                <SelectItem value="MEDIUM">{t("admin.crisis.sev.med")}</SelectItem>
                                                <SelectItem value="LOW">{t("admin.crisis.sev.low")}</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </Field>
                                    <Field>
                                        <FieldLabel htmlFor="edit-kw-resource">
                                            {t("admin.crisis.linkedResource")}
                                        </FieldLabel>
                                        <Select value={editResourceId} onValueChange={setEditResourceId}>
                                            <SelectTrigger id="edit-kw-resource" className="w-full">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {resources.map((r) => (
                                                    <SelectItem key={r.id} value={r.id}>
                                                        {r.phoneNumber ? `${r.phoneNumber} · ` : ""}
                                                        {r.email ?? "No contact info"}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </Field>
                                </FieldGroup>
                            </FieldSet>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setEditKeyword(null)}>
                                    Cancel
                                </Button>
                                <Button disabled={!editValue.trim() || updating} onClick={handleUpdate}>
                                    {updating ? "Saving…" : "Save"}
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>

                    {/* Delete confirmation dialog */}
                    <DeleteDialog
                        open={!!deleteKeywordTarget}
                        onOpenChange={(open) => {
                            if (!open) setDeleteKeywordTarget(null);
                        }}
                        title={t("admin.crisis.del")}
                        deleteTarget={deleteKeywordTarget?.value}
                        onConfirm={handleDelete}
                        loading={deleting}
                    />
                </TabsContent>
                <TabsContent value="resources">
                    <CrisisResources
                        search={resourceSearch}
                        onSearchChange={setResourceSearch}
                        view={resourcesView}
                        resultsLabel={
                            filteredResources.length === 0
                                ? "0 results"
                                : `${resourcesView.from}–${resourcesView.to} of ${filteredResources.length}`
                        }
                        selected={selectedResources}
                        onEdit={openEditResource}
                        onDelete={setDeleteResourceTarget}
                    />

                    {/* New resource dialog */}
                    <Dialog
                        open={newResourceOpen}
                        onOpenChange={(open) => {
                            setNewResourceOpen(open);
                            if (!open) {
                                setNewResourceMessage("");
                                setNewResourceEmail("");
                                setNewResourcePhoneNumber("");
                                setNewResourceError(null);
                            }
                        }}
                    >
                        <DialogContent className="max-w-lg">
                            <DialogHeader>
                                <DialogTitle>{t("admin.crisis.newResource")}</DialogTitle>
                                <DialogDescription>{t("admin.crisis.newResourceDesc")}</DialogDescription>
                            </DialogHeader>
                            <FieldSet>
                                <FieldGroup>
                                    <Field data-invalid={!!newResourceError}>
                                        <FieldLabel htmlFor="new-res-message" required>
                                            {t("common.fields.message")}
                                        </FieldLabel>
                                        <Textarea
                                            id="new-res-message"
                                            placeholder="e.g. Call or text 988 for the Suicide & Crisis Lifeline"
                                            rows={3}
                                            value={newResourceMessage}
                                            onChange={(e) => {
                                                setNewResourceMessage(e.target.value);
                                                setNewResourceError(null);
                                            }}
                                            aria-invalid={!!newResourceError}
                                            autoFocus
                                        />
                                    </Field>
                                    <Field data-invalid={!!newResourceError}>
                                        <FieldLabel htmlFor="new-res-email" optional>
                                            {t("common.fields.email")}
                                        </FieldLabel>
                                        <Input
                                            id="new-res-email"
                                            placeholder="e.g. crisis@example.com"
                                            value={newResourceEmail}
                                            onChange={(e) => {
                                                setNewResourceEmail(e.target.value);
                                                setNewResourceError(null);
                                            }}
                                            aria-invalid={!!newResourceError}
                                            type="email"
                                        />
                                    </Field>
                                    <Field>
                                        <FieldLabel htmlFor="new-res-phone" optional>
                                            {t("common.fields.phone")}
                                        </FieldLabel>
                                        <Input
                                            id="new-res-phone"
                                            placeholder="e.g. 988"
                                            value={newResourcePhoneNumber}
                                            onChange={(e) => setNewResourcePhoneNumber(e.target.value)}
                                        />
                                    </Field>
                                    <FieldError
                                        errors={newResourceError ? [{ message: newResourceError }] : undefined}
                                    />
                                </FieldGroup>
                            </FieldSet>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setNewResourceOpen(false)}>
                                    {t("common.actions.cancel")}
                                </Button>
                                <Button
                                    disabled={!newResourceMessage.trim() || creatingResource}
                                    onClick={handleCreateResource}
                                >
                                    {creatingResource ? "Saving…" : "Add resource"}
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>

                    {/* Edit resource dialog */}
                    <Dialog
                        open={!!editResource}
                        onOpenChange={(open) => {
                            if (!open) setEditResource(null);
                        }}
                    >
                        <DialogContent className="max-w-lg">
                            <DialogHeader>
                                <DialogTitle>{t("common.actions.editVariable", { variable: "resource" })}</DialogTitle>
                                <DialogDescription>
                                    Update this resource's message, email, or phone number.
                                </DialogDescription>
                            </DialogHeader>
                            <FieldSet>
                                <FieldGroup>
                                    <Field data-invalid={!!editResourceError}>
                                        <FieldLabel htmlFor="edit-res-message" required>
                                            {t("common.fields.message")}
                                        </FieldLabel>
                                        <Textarea
                                            id="edit-res-message"
                                            rows={3}
                                            value={editResourceMessage}
                                            onChange={(e) => {
                                                setEditResourceMessage(e.target.value);
                                                setEditResourceError(null);
                                            }}
                                            aria-invalid={!!editResourceError}
                                            autoFocus
                                        />
                                    </Field>
                                    <Field data-invalid={!!editResourceError}>
                                        <FieldLabel htmlFor="edit-res-email" optional>
                                            {t("common.fields.email")}
                                        </FieldLabel>
                                        <Input
                                            id="edit-res-email"
                                            value={editResourceEmail}
                                            onChange={(e) => {
                                                setEditResourceEmail(e.target.value);
                                                setEditResourceError(null);
                                            }}
                                            aria-invalid={!!editResourceError}
                                            type="email"
                                        />
                                    </Field>
                                    <Field>
                                        <FieldLabel htmlFor="edit-res-phone" optional>
                                            {t("common.fields.phone")}
                                        </FieldLabel>
                                        <Input
                                            id="edit-res-phone"
                                            value={editResourcePhoneNumber}
                                            onChange={(e) => setEditResourcePhoneNumber(e.target.value)}
                                        />
                                    </Field>
                                    <FieldError
                                        errors={editResourceError ? [{ message: editResourceError }] : undefined}
                                    />
                                </FieldGroup>
                            </FieldSet>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setEditResource(null)}>
                                    {t("common.actions.cancel")}
                                </Button>
                                <Button
                                    disabled={!editResourceMessage.trim() || updatingResource}
                                    onClick={handleUpdateResource}
                                >
                                    {updatingResource ? "Saving…" : "Save"}
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>

                    {/* Delete resource confirmation dialog */}
                    <DeleteDialog
                        open={!!deleteResourceTarget}
                        onOpenChange={(open) => {
                            if (!open) setDeleteResourceTarget(null);
                        }}
                        title="Delete Crisis Resource"
                        deleteTarget={deleteResourceTarget?.message}
                        onConfirm={handleDeleteResource}
                        loading={deletingResource}
                    />
                </TabsContent>
            </Container>
        </Tabs>
    );
}
